import { Container, Eyebrow } from '@tt/design-system';
import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import { MosaicGallery } from '../../components/MosaicGallery';
import { mockRegistrations } from '../../data/mockRegistrations';
import { useRegistrationsStore } from '../../store/registrationsStore';

type ViewMode = 'trophy' | 'grid';

export default function Gallery() {
  const localRecords = useRegistrationsStore((s) => s.records);
  const allRecords = useMemo(() => {
    // locais (mais recentes) primeiro, depois mocks ordenados por createdAt desc
    const sortedMocks = [...mockRegistrations].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    return [...localRecords, ...sortedMocks];
  }, [localRecords]);

  const [mode, setMode] = useState<ViewMode>('trophy');
  const [filter, setFilter] = useState('');

  const toggleBtn = (m: ViewMode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(m)}
      aria-pressed={mode === m}
      className={clsx(
        'rounded-md border px-4 py-2 font-mono text-xs uppercase tracking-stamp transition-colors duration-fast',
        mode === m
          ? 'border-gold bg-gold/10 text-gold shadow-goldSoft'
          : 'border-gold/30 text-champagne hover:border-gold hover:text-gold',
      )}
    >
      {label}
    </button>
  );

  return (
    <Container size="wide" className="py-16 md:py-24">
      <div className="mb-12 text-center">
        <Eyebrow tone="gold" className="mb-3">
          A taça das pessoas
        </Eyebrow>
        <h1 className="font-display text-3xl text-parchment md:text-5xl">
          A galeria completa.
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-neutral-100 md:text-base">
          Cada rosto é uma presença real, eternizada. Clique em qualquer foto para abrir o
          certificado público.
        </p>
      </div>

      <div className="mx-auto mb-10 flex max-w-3xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">{toggleBtn('trophy', 'Mosaico')}{toggleBtn('grid', 'Grid')}</div>

        <label className="flex flex-1 items-center gap-2 rounded-md border border-gold/20 bg-night/40 px-3 py-2 transition-colors duration-fast focus-within:border-gold">
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 flex-shrink-0 text-champagne"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="6" />
            <path d="M16 16l5 5" />
          </svg>
          <span className="sr-only">Buscar presença</span>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Buscar por nome ou frase…"
            className="min-w-0 flex-1 bg-transparent text-sm text-parchment placeholder:text-neutral-300/60 focus:outline-none"
          />
          {filter ? (
            <button
              type="button"
              onClick={() => setFilter('')}
              className="font-mono text-[10px] uppercase tracking-stamp text-champagne hover:text-gold"
              aria-label="Limpar busca"
            >
              limpar
            </button>
          ) : null}
        </label>
      </div>

      <MosaicGallery records={allRecords} mode={mode} filter={filter} pageSize={36} />

      <p className="mt-10 text-center font-mono text-xs uppercase tracking-stamp text-champagne/70">
        {allRecords.length} presenças registradas · atualizado em tempo real
      </p>
    </Container>
  );
}
